import type {
  AuctionBlueprintOnlineReactiveArgs,
  AuctionBlueprintScheduledOnlineReactiveArgs,
} from '@adc/blueprint'
import type { FeatureFlags } from '@adc/ui-adc'
import * as graph from '@adc/sdk'
import type { Maybe } from '@adc/sdk/types'
import { isPendingFclOnlineDeposit } from 'common/utils/listing'

const USER_WITH_PROXY_BID = 'user-with-proxy-bid'
const USER_WITHOUT_PROXY_BID = 'user-without-proxy-bid'
const STARTED = 'started'
const HIGHEST_BIDDER_RESERVE_MET = 'highest-bidder-reserve-met'
const HIGHEST_BIDDER_RESERVE_NOT_MET = 'highest-bidder-reserve-not-met'
const OUTBID_RESERVE_MET = 'outbid-reserve-met'
const OUTBID_RESERVE_NOT_MET = 'outbid-reserve-not-met'
const COUNTERBID = 'counterbid'
const OTHER = 'other'
const USER_WON_MANUALLY_SUBJECT_TO_CONFIRMATION = 'won-manually-subject-to'
const USER_WON_MANUALLY_OUTRIGHT = 'won-manually-outright'
const USER_WON_SUBJECT_TO_APPROVAL = 'won-reserve-not-met'
const USER_WON_SUBJECT_TO_CONFIRMATION = 'won-reserve-met-subject-to'
const USER_WON_OUTRIGHT = 'won-reserve-met'
const USER_LOST_RESERVE_NOT_MET = 'lost-reserve-not-met-not-outbid'
const USER_LOST_RESERVE_MET = 'lost-reserve-met-not-outbid'
const USER_CAN_SUBMIT_FINAL_OFFER = 'can-submit-final-offer'
const FINAL_OFFER_PENDING = 'final-offer-pending'

export const BID_BANNER_STATES = {
  USER_WITH_PROXY_BID,
  USER_WITHOUT_PROXY_BID,
  STARTED,
  HIGHEST_BIDDER_RESERVE_MET,
  HIGHEST_BIDDER_RESERVE_NOT_MET,
  OUTBID_RESERVE_MET,
  OUTBID_RESERVE_NOT_MET,
  COUNTERBID,
  OTHER,
  USER_WON_MANUALLY_SUBJECT_TO_CONFIRMATION,
  USER_WON_MANUALLY_OUTRIGHT,
  USER_WON_SUBJECT_TO_APPROVAL,
  USER_WON_SUBJECT_TO_CONFIRMATION,
  USER_WON_OUTRIGHT,
  USER_LOST_RESERVE_NOT_MET,
  USER_LOST_RESERVE_MET,
  USER_CAN_SUBMIT_FINAL_OFFER,
  FINAL_OFFER_PENDING,
}

interface GetBidBannerStateBeforeAuctionProps {
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
}

const getBidBannerStateBeforeAuction = ({
  segment,
}: GetBidBannerStateBeforeAuctionProps) => {
  if (segment.__methods__.isMaxBiddingActive()) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    return segment && segment.__methods__?.hasValidMaxBid?.()
      ? USER_WITH_PROXY_BID
      : USER_WITHOUT_PROXY_BID
  } else {
    return OTHER
  }
}

const getBidBannerStateDuringAuction = ({
  segment,
}: {
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
}) => {
  if (
    segment.current_user_bidding_status ===
    graph.enums.OnlineAuctionSegmentBiddingStatus.HighestBid
  ) {
    return segment.reserve_status === graph.enums.ReserveStatus.ReserveMet
      ? HIGHEST_BIDDER_RESERVE_MET
      : HIGHEST_BIDDER_RESERVE_NOT_MET
  } else if (
    segment.current_user_bidding_status ===
    graph.enums.OnlineAuctionSegmentBiddingStatus.Outbid
  ) {
    if (
      segment.current_highest_bid?.type ===
      graph.enums.OnlineAuctionSegmentBidType.Seller
    ) {
      return COUNTERBID
    }
    return segment.reserve_status === graph.enums.ReserveStatus.ReserveMet
      ? OUTBID_RESERVE_MET
      : OUTBID_RESERVE_NOT_MET
  } else {
    return STARTED
  }
}

interface SharedBidBannerStateProps {
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
}

const getBidBannerStatusUserHasWon = ({
  segment,
}: SharedBidBannerStateProps) => {
  if (
    segment.current_highest_bid?.type ===
    graph.enums.OnlineAuctionSegmentBidType.Manual
  ) {
    // to replace bStatus.wasManuallyClerked, we look for current user biding status is won and that the highest bid is manual
    return segment.subject_to_status === graph.enums.SubjectToStatus.Pending // replacing bStatus.isSoldSubjectTo
      ? USER_WON_MANUALLY_SUBJECT_TO_CONFIRMATION
      : USER_WON_MANUALLY_OUTRIGHT
  } else if (
    segment.subject_to_status === graph.enums.SubjectToStatus.Pending
  ) {
    return segment.reserve_status === graph.enums.ReserveStatus.ReserveMet
      ? USER_WON_SUBJECT_TO_CONFIRMATION
      : USER_WON_SUBJECT_TO_APPROVAL
  } else {
    return USER_WON_OUTRIGHT
  }
}

const getBidBannerStateAfterAuction = ({
  segment,
}: SharedBidBannerStateProps) => {
  if (
    segment.current_user_bidding_status ===
    graph.enums.OnlineAuctionSegmentBiddingStatus.Won
  ) {
    return getBidBannerStatusUserHasWon({ segment })
  } else if (
    segment.segment_status === graph.enums.OnlineAuctionSegmentStatus.Ended
  ) {
    return segment.reserve_status === graph.enums.ReserveStatus.ReserveMet
      ? USER_LOST_RESERVE_MET
      : USER_LOST_RESERVE_NOT_MET
  } else {
    return OTHER
  }
}

// TODO: make this a single object param instead of a bunch of args
export const getBidBannerState = (
  segment: Maybe<AuctionBlueprintScheduledOnlineReactiveArgs['segment']>,
  listing: AuctionBlueprintOnlineReactiveArgs['listing'],
  featureFlags: FeatureFlags = {}
) => {
  if (!segment) {
    return OTHER
  }

  const isRegisteredOrPendingDeposit =
    listing?.is_current_user_registered_for_online_auction ||
    isPendingFclOnlineDeposit(listing, featureFlags)

  if (!isRegisteredOrPendingDeposit) {
    if (
      // TODO: test suspending a user who wins the auction from qualdash while looking at the PDP screen and see what the behavior is to see if we need this block below, otherwise can remove it
      segment.current_user_bidding_status ===
      graph.enums.OnlineAuctionSegmentBiddingStatus.Won
    ) {
      return getBidBannerStatusUserHasWon({ segment })
    }
    return OTHER
  } else if (
    segment.segment_status === graph.enums.OnlineAuctionSegmentStatus.Pending
  ) {
    return getBidBannerStateBeforeAuction({ segment })
  } else {
    return segment.__methods__.isOnlineAuctionActive()
      ? getBidBannerStateDuringAuction({ segment })
      : getBidBannerStateAfterAuction({ segment })
  }
}
